import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import {
  UserSearch, GraduationCap, Rocket, ArrowRight, CheckCircle2, ShieldCheck,
  Clock, RefreshCw, Briefcase, Layers, Network, Quote
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-white bg-white/10 border border-white/15 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#8BD835]"/>
    {children}
  </span>
);

const PIPELINE = [
  { step: "01", title: "Hire", icon: UserSearch, desc: "We source and screen candidates against your exact role, skill and culture-fit criteria from a vetted talent pool." },
  { step: "02", title: "Train", icon: GraduationCap, desc: "Candidates complete a job-simulation curriculum on your tech stack &mdash; assessed continuously against real delivery benchmarks." },
  { step: "03", title: "Deploy", icon: Rocket, desc: "Job-ready talent joins your team or engagement, productive from week one, with a performance-backed guarantee." }
];

const ENGAGEMENT_MODELS = [
  { title: "Staff Augmentation", desc: "Extend your existing team with pre-trained talent that plugs directly into your sprints and tooling.", icon: Briefcase },
  { title: "Dedicated Pods", desc: "A fully-formed, SkillsGain-managed squad delivering against your roadmap under a single point of accountability.", icon: Layers },
  { title: "Project-Based Deployment", desc: "Ring-fenced talent deployed against a scoped project with fixed milestones and delivery SLAs.", icon: Network }
];

const GUARANTEES = [
  { value: "6–8 wks", label: "Bench-to-Billable Time", icon: Clock },
  { value: "30-day", label: "Replacement Guarantee", icon: RefreshCw },
  { value: "95%", label: "Client Retention Rate", icon: ShieldCheck }
];

const SKILL_TRACKS = ["Cloud & DevOps", "Full-Stack Engineering", "Data & AI", "Quality Engineering", "Cybersecurity", "Salesforce & ERP"];

const HireTrainDeployPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0A1F5C] via-[#15348C] to-[#1E47A8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "26px 26px" }}
        />
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-[100px]"/>
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-[100px]"/>
        <div className="relative max-w-5xl mx-auto px-4 py-24 lg:py-28 text-center">
          <Reveal>
            <Eyebrow>HIRE &bull; TRAIN &bull; DEPLOY</Eyebrow>
            <h1 className="mt-6 text-4xl lg:text-6xl font-bold leading-[1.1]">Build your bench before you need it.</h1>
            <p className="mt-5 text-gray-300 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              A managed talent pipeline that sources, trains and deploys job-ready engineers against your exact stack &mdash; on a predictable timeline, with a performance guarantee.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Build My Talent Pipeline <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pipeline steps */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">One pipeline, three accountable stages.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6 relative">
            <div className="hidden lg:block absolute top-[3.75rem] left-[20%] right-[20%] h-0.5 border-t-2 border-dashed border-[#15348C]/20"/>
            {PIPELINE.map((p, i) => (
              <Reveal key={p.title} variant="up" delay={i * 100}>
                <div className="relative text-center lg:text-left">
                  <div className="mx-auto lg:mx-0 w-16 h-16 rounded-2xl bg-[#15348C] flex items-center justify-center relative z-10 shadow-lg">
                    <p.icon size={28} className="text-white"/>
                  </div>
                  <div className="mt-5 text-xs font-bold tracking-widest text-[#F57C20]">STEP {p.step}</div>
                  <h3 className="text-xl font-bold text-[#15348C] mt-1">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-gray-500 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Skill tracks */}
      <section className="bg-[#F8F9FA] py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-xs font-bold tracking-widest text-[#15348C] mb-5">TALENT AVAILABLE ACROSS</div>
          <div className="flex flex-wrap justify-center gap-3">
            {SKILL_TRACKS.map(s => (
              <span key={s} className="bg-white border border-gray-200 text-[#15348C] text-sm font-semibold px-4 py-2 rounded-full shadow-sm">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement models */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Choose how the pipeline plugs into your team.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-6">
            {ENGAGEMENT_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 100}>
                <div className="sg-card group bg-white border border-gray-200/80 rounded-2xl p-8 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <m.icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <h4 className="font-bold text-[#15348C] text-lg">{m.title}</h4>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees strip + testimonial */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-5 gap-6">
          <Reveal variant="left" className="lg:col-span-2 grid grid-rows-3 gap-4">
            {GUARANTEES.map(g => (
              <div key={g.label} className="sg-card bg-white border border-gray-200/80 rounded-xl p-5 flex items-center gap-4 shadow-sm">
                <div className="w-11 h-11 shrink-0 rounded-lg bg-[#15348C] flex items-center justify-center">
                  <g.icon size={20} className="text-white"/>
                </div>
                <div>
                  <div className="text-xl font-bold text-[#15348C]">{g.value}</div>
                  <div className="text-xs text-gray-500">{g.label}</div>
                </div>
              </div>
            ))}
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-3">
            <div className="bg-white border border-gray-200/80 rounded-2xl p-8 lg:p-10 h-full flex flex-col shadow-sm">
              <Quote size={30} className="text-[#F57C20] mb-4"/>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                SkillsGain&rsquo;s Hire-Train-Deploy model gave us a predictable, always-on bench of cloud engineers. We cut our vendor hiring cycle from four months to under eight weeks, without compromising on quality.
              </p>
              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="font-bold text-[#15348C]">VP of Engineering</div>
                <div className="text-sm text-gray-500">Global Financial Services Enterprise</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR TALENT &amp; DELIVERY LEADERS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Never let hiring block delivery again.</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Tell us your skill demand and timelines &mdash; we&rsquo;ll design a pipeline that keeps pace with your roadmap.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Vetted, job-ready talent", "Replacement guarantee", "Dedicated delivery manager"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request a Pipeline Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default HireTrainDeployPage;
